import { of } from 'rxjs';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, catchError, exhaustMap, switchMap, tap } from 'rxjs/operators';
import { UserInfoService } from '@appStore/account/services/user-info.service';

import * as actions from '@appStore/account/actions';

import { UserRegistrationActionTypes } from '@appStore/account/actions';

@Injectable()
export class UserRegistrationEffects {
  constructor(private router: Router, private userinfoService: UserInfoService, private actions$: Actions) {}

  updateRegistration$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserRegistrationActionTypes.UserRegistrationUpdateItem),
      map((action: actions.UserRegistrationUpdate) => action.payload),
      exhaustMap(registration => {
        if (!registration.managerCompanyId) {
          return this.userinfoService.updateUserRegistration(registration).pipe(
            switchMap(item => [new actions.UserRegistrationUpdateSuccess()]),
            catchError(error => of(new actions.UserRegistrationUpdateFail(error)))
          );
        } else {
          return this.userinfoService.addCustomerToManagerCompany(registration).pipe(
            switchMap(item => [new actions.UserRegistrationUpdateSuccess()]),
            catchError(error => of(new actions.UserRegistrationUpdateFail(error)))
          );
        }
      })
    );
  });

  autoRegistration$ = createEffect(() => {
    return  this.actions$.pipe(
      ofType(UserRegistrationActionTypes.AutoUserRegistration),
      map((action: actions.AutoUserRegistration) => action.payload),
      exhaustMap(registration => {
        return this.userinfoService.autoUserRegistration(registration).pipe(
          switchMap(item => [new actions.AutoUserRegistrationSuccess()]),
          catchError(error => of(new actions.AutoUserRegistrationFail(error)))
        );
      })
    );
  });

  updateAdditionalRegistration$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserRegistrationActionTypes.AdditionalCustomerRegistration),
      map((action: actions.AdditionalCustomerRegistration) => action.payload),
      exhaustMap(registration =>
        this.userinfoService.updateUserRegistration(registration).pipe(
          map(item => new actions.AdditionalCustomerRegistrationSuccess()),
          catchError(error => of(new actions.AdditionalCustomerRegistrationFail(error)))
        )
      )
    );
  });
  
  isCustomerUserTypeValid$ = createEffect(() => {
    return  this.actions$.pipe(
      ofType(UserRegistrationActionTypes.IsCustomerUserTypeValid),
      map((action: actions.IsCustomerUserTypeValid) => action.payload),
      exhaustMap(item =>
        this.userinfoService.getIsCustomerUserTypeValid(item).pipe(
          map(validItem => new actions.IsCustomerUserTypeValidSuccess(validItem)),
          catchError(error => of(new actions.IsCustomerUserTypeValidFail(error)))
        )
      )
    );
  });

  isCustomerMeteringPointsValid$ = createEffect(() => {
    return  this.actions$.pipe(
      ofType(UserRegistrationActionTypes.IsCustomerMeteringPointsValid),
      map((action: actions.IsCustomerMeteringPointsValid) => action.payload),
      exhaustMap(item =>
        this.userinfoService.getIsCustomerMeteringPointsValid(item).pipe(
          map(validItem => new actions.IsCustomerMeteringPointsValidSuccess(validItem)),
          catchError(error => of(new actions.IsCustomerMeteringPointsValidFail(error)))
        )
      )
    );
  });
 
  isManagerCpompanyOrgNumberValid$ = createEffect(() => {
    return   this.actions$.pipe(
      ofType(UserRegistrationActionTypes.IsManagerCompanyOrgNumberExistingCustomer),
      map((action: actions.IsManagerCompanyOrgNumberExistingCustomer) => action.payload),
      exhaustMap(item =>
        this.userinfoService.getIsManagerCompanyOrganisationNumberAlsoCustomer(item).pipe(
          map(validItem => new actions.IsManagerCompanyOrgNumberExistingCustomerSuccess(validItem)),
          catchError(error => of(new actions.IsManagerCompanyOrgNumberExistingCustomerFail(error)))
        )
      )
    );
  });

  isRegisteredOnSegmentValid$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserRegistrationActionTypes.IsRegisteredOnSegment),
      map((action: actions.IsRegisteredOnSegment) => action.payload),
      exhaustMap(item =>
        this.userinfoService.getIsRegisteredOnSegmentValid(item).pipe(
          map(validItem => new actions.IsRegisteredOnSegmentSuccess(validItem)),
          catchError(error => of(new actions.IsRegisteredOnSegmentFail(error)))
        )
      )
    );
  });

  isRegisteredOnCompanyValid$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserRegistrationActionTypes.IsRegisteredOnCompany),
      map((action: actions.IsRegisteredOnCompany) => action.payload),
      exhaustMap(item =>
        this.userinfoService.getIsRegisteredOnCompanyValid(item).pipe(
          map(validItem => new actions.IsRegisteredOnCompanySuccess(validItem)),
          catchError(error => of(new actions.IsRegisteredOnCompanyFail(error)))
        )
      )
    );
  });
  
  createManagerCompany$ = createEffect(() => {
    return  this.actions$.pipe(
      ofType(UserRegistrationActionTypes.CreateManagerCompany),
      map((action: actions.CreateManagerCompany) => action.payload),
      exhaustMap(item =>
        this.userinfoService.createManagerCompany(item).pipe(
          map(validItem => new actions.CreateManagerCompanySuccess(validItem)),
          catchError(error => of(new actions.CreateManagerCompanyFail(error)))
        )
      )
    );
  });
 
  handleCreateManagerCompanySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserRegistrationActionTypes.CreateManagerCompanySuccess),
      map((action: actions.CreateManagerCompany) => action.payload),
      tap(item => this.router.navigate([`/secure/register/${item.id}/edit`]))
    ),
    { dispatch: false }
  );

  createAdditionalManagerCompany$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserRegistrationActionTypes.CreateAdditionalManagerCompany),
      map((action: actions.CreateAdditionalManagerCompany) => action.payload),
      exhaustMap(item =>
        this.userinfoService.createManagerCompany(item).pipe(
          map(validItem => new actions.CreateAdditionalManagerCompanySuccess(validItem)),
          catchError(error => of(new actions.CreateAdditionalManagerCompanyFail(error)))
        )
      )
    );
  });
  
  loadCustomerUserType$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserRegistrationActionTypes.CustomerUserTypeLoadData),
      exhaustMap((action: actions.CustomerUserTypeLoad) => {
        return this.userinfoService.getCustomerUserType(action.payload).pipe(
          map(validItem => new actions.CustomerUserTypeLoadSuccess(validItem)),
          catchError(error => of(new actions.CustomerUserTypeLoadFail(error)))
        );
      })
    );
  });
}
